"use client";

import {
  AlertTriangle,
  BellRing,
  Check,
  ChevronDown,
  Clock3,
  FilePenLine,
  FlaskConical,
  HeartPulse,
  ListPlus,
  Sparkles,
  Wand2,
} from "lucide-react";
import { useEffect, useState } from "react";
import type { ParsedOrder, SoapSection } from "@/src/services/mockAiService";
import { useLang } from "@/src/i18n";

type FeedItem = {
  id: string;
  type: "warning" | "success" | "info";
  title: string;
  body: string;
  details: string;
  evidence: string;
  soap: { field: SoapSection; text: string };
  order: Omit<ParsedOrder, "id" | "orderedBy" | "orderedAt">;
  status?: "handled" | "snoozed";
  soapStaged?: boolean;
  orderStaged?: boolean;
};

type HeartbeatFeedProps = {
  onSendToSoap: (action: { field: SoapSection; text: string; title: string; evidence: string }) => void;
  onDraftOrder: (order: ParsedOrder, source: { title: string; evidence: string }) => void;
};

const initialItems: FeedItem[] = [
  {
    id: "interaction",
    type: "warning",
    title: "Drug-Drug Interaction",
    body: "Warfarin + Amoxicillin may increase INR",
    details: "Consider closer INR monitoring if both medications are continued. Nong SATI is not changing the medication plan.",
    evidence: "MAR review · interaction rules v4",
    soap: {
      field: "plan",
      text: "Medication interaction reviewed: if Warfarin and Amoxicillin are continued, monitor INR more closely and reassess bleeding risk.",
    },
    order: {
      category: "lab",
      label: "PT/INR",
      details: "Tomorrow 08:00",
      lane: "one-day",
      stat: false,
      source: "heartbeat",
    },
  },
  {
    id: "vitals",
    type: "success",
    title: "Vitals Stabilizing",
    body: "SBP trending within 110–130 range",
    details: "Eight recent readings are stable with no new threshold breach.",
    evidence: "Bedside monitor · last 8 observations",
    soap: {
      field: "objective",
      text: "Hemodynamically stable; SBP remained within 110–130 mmHg across the last eight observations.",
    },
    order: {
      category: "other",
      label: "Vital signs monitoring",
      details: "Every 4 hours",
      lane: "continuous",
      stat: false,
      source: "heartbeat",
    },
  },
  {
    id: "troponin",
    type: "info",
    title: "Lab Reminder",
    body: "Troponin draw in 2 hours",
    details: "Scheduled order is due at 14:00. Responsibility remains with the care team.",
    evidence: "Order schedule · due 14:00",
    soap: {
      field: "plan",
      text: "Repeat troponin at 14:00 and review the trend with the current ECG.",
    },
    order: {
      category: "lab",
      label: "Troponin",
      details: "Today 14:00",
      lane: "one-day",
      stat: false,
      source: "heartbeat",
    },
  },
];

const icons = { warning: AlertTriangle, success: HeartPulse, info: FlaskConical };

export function HeartbeatFeed({ onSendToSoap, onDraftOrder }: HeartbeatFeedProps) {
  const { t } = useLang();
  const [items, setItems] = useState<FeedItem[]>(initialItems);
  const [expanded, setExpanded] = useState<string | null>("interaction");

  useEffect(() => {
    const interval = window.setInterval(() => {
      setItems((current) => {
        if (current.some((item) => item.id === "new-gap")) return current;
        return [
          {
            id: "new-gap",
            type: "info",
            title: "Documentation Gap",
            body: "Bleeding status is not yet explicit",
            details: "Claim-critical for Anemia. A grounded suggestion is available for clinician review.",
            evidence: "CBC 08:00 · Anemia evidence schema",
            soap: {
              field: "subjective",
              text: "No melena, hematemesis, hematochezia, or other active bleeding reported.",
            },
            order: {
              category: "lab",
              label: "Stool occult blood",
              details: "Next specimen",
              lane: "one-day",
              stat: false,
              source: "heartbeat",
            },
          },
          ...current,
        ];
      });
    }, 9000);
    return () => window.clearInterval(interval);
  }, []);

  function setStatus(id: string, status: "handled" | "snoozed") {
    setItems((current) => current.map((item) => (item.id === id ? { ...item, status } : item)));
  }

  function sendToSoap(item: FeedItem) {
    onSendToSoap({ ...item.soap, title: t(`hb.item.${item.id}.title`, item.title), evidence: t(`hb.item.${item.id}.evidence`, item.evidence) });
    setItems((current) => current.map((entry) => entry.id === item.id ? { ...entry, soapStaged: true } : entry));
  }

  function draftOrder(item: FeedItem) {
    onDraftOrder(
      { ...item.order, id: `feed-${item.id}-${Date.now()}` },
      { title: t(`hb.item.${item.id}.title`, item.title), evidence: t(`hb.item.${item.id}.evidence`, item.evidence) },
    );
    setItems((current) => current.map((entry) => entry.id === item.id ? { ...entry, orderStaged: true } : entry));
  }

  /** Auto-correct: send every active item's suggestion to SOAP and draft its order. */
  function applyAll() {
    const targets = items.filter((item) => !item.status);
    targets.forEach((item, index) => {
      window.setTimeout(() => {
        onSendToSoap({ ...item.soap, title: t(`hb.item.${item.id}.title`, item.title), evidence: t(`hb.item.${item.id}.evidence`, item.evidence) });
        onDraftOrder(
          { ...item.order, id: `feed-${item.id}-${Date.now()}-${index}` },
          { title: t(`hb.item.${item.id}.title`, item.title), evidence: t(`hb.item.${item.id}.evidence`, item.evidence) },
        );
      }, index * 60);
    });
    setItems((current) =>
      current.map((entry) => (entry.status ? entry : { ...entry, soapStaged: true, orderStaged: true })),
    );
  }

  const activeCount = items.filter((item) => !item.status).length;

  return (
    <section className="panel heartbeat-feed">
      <div className="panel-heading">
        <div><BellRing size={17} /><span>{t("hb.title")}</span></div>
        <div className="feed-heading-actions">
          <button
            type="button"
            className="feed-apply-all"
            onClick={applyAll}
            disabled={activeCount === 0}
            title={t("hb.autocorrect.title")}
          >
            <Wand2 size={13} /> {t("hb.autocorrect")}
          </button>
          <span className="feed-count">{activeCount}</span>
        </div>
      </div>
      <div className="feed-list">
        {items.map((item) => {
          const Icon = icons[item.type];
          const isOpen = expanded === item.id;
          return (
            <article className={`feed-card ${item.type} ${item.status ? "is-resolved" : ""}`} key={item.id}>
              <button
                type="button"
                className="feed-summary"
                onClick={() => setExpanded(isOpen ? null : item.id)}
                aria-expanded={isOpen}
              >
                <Icon size={16} />
                <span><strong>{t(`hb.item.${item.id}.title`, item.title)}</strong><small>{t(`hb.item.${item.id}.body`, item.body)}</small></span>
                <ChevronDown size={14} />
              </button>
              {isOpen && (
                <div className="feed-details">
                  <p>{t(`hb.item.${item.id}.details`, item.details)}</p>
                  <span className="feed-evidence">{t(`hb.item.${item.id}.evidence`, item.evidence)}</span>
                  <div className="feed-ai-actions">
                    <button type="button" className={item.soapStaged ? "is-added" : ""} onClick={() => sendToSoap(item)}>
                      {item.soapStaged ? <Check size={13} /> : <FilePenLine size={13} />}
                      {item.soapStaged ? t("hb.sentToSoap") : `${t("hb.sendTo")} ${t(`soap.${item.soap.field}`)}`}
                    </button>
                    <button type="button" className={item.orderStaged ? "is-added" : ""} onClick={() => draftOrder(item)}>
                      {item.orderStaged ? <Check size={13} /> : <ListPlus size={13} />}
                      {item.orderStaged ? t("hb.orderDrafted") : t("hb.draftOrder")}
                    </button>
                  </div>
                  {item.status ? (
                    <span className="status-label">
                      {item.status === "handled" ? <Check size={13} /> : <Clock3 size={13} />}
                      {t(`status.${item.status}`)}
                    </span>
                  ) : (
                    <div className="feed-resolution-actions">
                      <button type="button" onClick={() => setStatus(item.id, "handled")}><Check size={13} /> {t("hb.handled")}</button>
                      <button type="button" onClick={() => setStatus(item.id, "snoozed")}><Clock3 size={13} /> {t("hb.snooze")}</button>
                    </div>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>
      <div className="feed-footer"><Sparkles size={12} /> {t("hb.footer")}</div>
    </section>
  );
}
